import { ChevronDown, X } from "lucide-react";
import { Icon, IconButton, Spinner, cn } from "@winche/ui";
import { ModelSearch, type OrModel } from "./ModelSearch";

/**
 * One model slot (free model, paid fallback): the current id in a field-like
 * button that opens the model search, and an icon button to clear it
 * (admin-17, admin-M3: 44 px with a finger, a spinner while saving, no "…").
 */
export function ModelSlot({
  id,
  label,
  value,
  placeholder,
  open,
  saving,
  highlight = false,
  onOpenChange,
  onSelect,
  onClear,
  clearLabel,
  search,
}: {
  id: string;
  label: string;
  value: string | null;
  placeholder: string;
  open: boolean;
  saving: boolean;
  /** Tinted border when the slot is in use (free model on). */
  highlight?: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (model: OrModel) => void;
  onClear?: () => void;
  clearLabel?: string;
  search: { freeOnly?: boolean; paidOnly?: boolean; visionOnly?: boolean };
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <div className="relative min-w-0 flex-1">
        <button
          id={id}
          type="button"
          aria-label={`${label}: ${value ?? placeholder}`}
          aria-expanded={open}
          onClick={() => onOpenChange(!open)}
          disabled={saving}
          className={cn(
            "flex min-h-11 w-full items-center gap-2 rounded-md border bg-background px-3 text-left text-base transition-colors duration-150 hover:bg-hover disabled:cursor-not-allowed md:pointer-fine:min-h-9 md:pointer-fine:text-sm",
            highlight ? "border-success-border" : "border-input",
            value ? "text-foreground" : "text-muted-foreground",
          )}
        >
          <span className="min-w-0 flex-1 truncate">{value ?? placeholder}</span>
          {saving ? <Spinner size="xs" /> : <Icon icon={ChevronDown} size="sm" className="text-subtle-foreground" />}
        </button>
        {open && (
          <ModelSearch
            freeOnly={search.freeOnly}
            paidOnly={search.paidOnly}
            visionOnly={search.visionOnly}
            selectedId={value}
            onSelect={onSelect}
            onClose={() => onOpenChange(false)}
          />
        )}
      </div>
      {onClear && (
        <IconButton label={clearLabel ?? `Clear ${label}`} variant="ghost" size="icon-sm" onClick={onClear} disabled={saving}>
          <Icon icon={X} size="sm" />
        </IconButton>
      )}
    </div>
  );
}
